import React from 'react';
import { Box, Typography, Button, Paper, Stack, Collapse } from '@mui/material';
import { createErrorHandler, ErrorCategory, ErrorSeverity, captureError } from '../lib/errorReporting';

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays fallback UI
 * Integrates with error reporting service for production monitoring
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false,
    };
    
    // Create error handler for this boundary
    this.errorHandler = createErrorHandler(props.name || 'ErrorBoundary');
  }

  static getDerivedStateFromError(error) {
    // Update state so next render shows fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Update state with error info
    this.setState({ errorInfo });
    
    // Call props callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
    
    // Report to error monitoring service
    this.errorHandler(error, errorInfo);
    
    // Additional capture with more context
    captureError(error, {
      category: ErrorCategory.UI,
      severity: this.props.critical ? ErrorSeverity.FATAL : ErrorSeverity.ERROR,
      extra: {
        componentStack: errorInfo?.componentStack,
        boundaryName: this.props.name,
        props: this.getSafeProps(),
      },
      tags: ['error-boundary', this.props.name].filter(Boolean),
    });
  }

  /**
   * Get safe props for error reporting (exclude children and functions)
   */
  getSafeProps() {
    const { children, onError, fallback, ...safeProps } = this.props;
    const result = {};
    for (const [key, value] of Object.entries(safeProps)) {
      if (typeof value !== 'function' && typeof value !== 'object') {
        result[key] = value;
      }
    }
    return result;
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  toggleDetails = () => {
    this.setState(state => ({ showDetails: !state.showDetails }));
  };

  render() {
    const { hasError, error, errorInfo, showDetails } = this.state;
    const { fallback, children, showResetButton = true } = this.props;
    const isDev = process.env.NODE_ENV !== 'production';

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback({ error, errorInfo, reset: this.handleReset });
        }
        return fallback;
      }

      // Default fallback UI
      return (
        <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
          <Paper sx={{ p: 3 }} elevation={2}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" color="error" gutterBottom fontWeight={600}>
                  Something went wrong
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  An unexpected error occurred. We've been notified and are working to fix it.
                </Typography>
              </Box>

              <Stack direction="row" spacing={2}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>
                
                {showResetButton && (
                  <Button 
                    variant="outlined" 
                    onClick={this.handleReset}
                  >
                    Try Again
                  </Button>
                )}
              </Stack>

              {/* Show error details in development */}
              {isDev && error && (
                <Box>
                  <Button 
                    size="small" 
                    onClick={this.toggleDetails}
                    sx={{ mb: 1 }}
                  >
                    {showDetails ? 'Hide Details' : 'Show Details'}
                  </Button>
                  
                  <Collapse in={showDetails}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'grey.50',
                        overflow: 'auto',
                        maxHeight: 400,
                      }}
                    >
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Error: {error.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        component="pre" 
                        sx={{ 
                          whiteSpace: 'pre-wrap', 
                          wordBreak: 'break-word',
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                        }}
                      >
                        {error.message}
                      </Typography>
                      
                      {error.stack && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            Stack Trace:
                          </Typography>
                          <Typography 
                            variant="body2" 
                            component="pre" 
                            sx={{ 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              fontFamily: 'monospace',
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                            }}
                          >
                            {error.stack}
                          </Typography>
                        </Box>
                      )}
                      
                      {errorInfo?.componentStack && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" gutterBottom>
                            Component Stack:
                          </Typography>
                          <Typography 
                            variant="body2" 
                            component="pre" 
                            sx={{ 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              fontFamily: 'monospace',
                              fontSize: '0.7rem',
                              color: 'text.secondary',
                            }}
                          >
                            {errorInfo.componentStack}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Collapse>
                </Box>
              )}
            </Stack>
          </Paper>
        </Box>
      );
    }

    return children;
  }
}

/**
 * Higher-order component to wrap a component with error boundary
 * @param {React.Component} Component - Component to wrap
 * @param {Object} options - Error boundary options
 * @returns {React.Component} Wrapped component
 */
export function withErrorBoundary(Component, options = {}) {
  const { name, ...boundaryProps } = options;
  const displayName = name || Component.displayName || Component.name || 'Component';
  
  function WithErrorBoundary(props) {
    return (
      <ErrorBoundary name={displayName} {...boundaryProps}>
        <Component {...props} />
      </ErrorBoundary>
    );
  }
  
  WithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;
  return WithErrorBoundary;
}
